import { Component, DestroyRef, ElementRef, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { GeneralService } from 'src/app/services/general.service';
import { HelperService } from 'src/app/services/helper.service';
import { AuthService } from 'src/app/services/auth.service';
import { getRoleContext, userRoleContext, getHomeUrlForRole } from 'src/app/constants/role-details.constants';

interface LoggedInUser {
  userId: string;
  userName: string;
  userRole: number[];
  userRoleNames?: string[];
  rolePermissionTypes?: number[];
  contactUsEmail?: string;
  myExostar?: string;
}

interface SettingValueResponse {
  success: boolean;
  data?: string;
  error?: string;
}

const ROLE_LABELS: Record<number, string> = {
  0: 'Supplier Submitter',
  1: 'Supplier Administrator',
  2: 'Buyer User',
  3: 'Buyer User with FSS Permission',
  4: 'Buyer Technical Administrator',
  5: 'Buyer Function Administrator',
  6: 'Support Coordinator',
  7: 'Support Administrator',
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly generalService = inject(GeneralService);
  private readonly helperService = inject(HelperService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);

  private objectUrl: string | null = null;

  readonly logo = signal<string | null>(null);
  readonly showDropdown = signal(false);
  readonly applicationName = signal('Supplier Management');
  readonly isTcRoute = signal(this.router.url.includes('terms-and-condition'));

  readonly userDetails: LoggedInUser | null = this.readUserDetails();
  readonly userRoleContext = userRoleContext;
  readonly roleContext = getRoleContext();
  readonly homeUrl = getHomeUrlForRole();
  readonly roleDisplayName = computed(() => {
    // Use userRoleNames from API which contains actual display names (e.g., "Super User")
    // instead of ROLE_LABELS lookup which shows "Dynamic" for dynamic roles
    const roleName = this.userDetails?.userRoleNames?.[0];
    if (roleName) {
      return roleName;
    }
    // Fallback to ROLE_LABELS lookup if userRoleNames not available
    const role = this.userDetails?.userRole?.[0];
    return role !== undefined ? (ROLE_LABELS[role] ?? '') : '';
  });
  readonly myExostarUrl = computed(() => this.userDetails?.myExostar ?? '#');
  readonly contactUsEmail = computed(() => this.userDetails?.contactUsEmail ?? '');

  /** Close dropdown when clicking outside the user dropdown area (SM-3337) */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showDropdown()) return;

    const target = event.target as HTMLElement;
    const dropdown = this.elementRef.nativeElement.querySelector('.welcome-user');
    if (dropdown && !dropdown.contains(target)) {
      this.showDropdown.set(false);
    }
  }

  ngOnInit(): void {
    this.loadLogo();
    this.loadApplicationName();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(e => {
        this.isTcRoute.set(e.urlAfterRedirects.includes('terms-and-condition'));
        // Close user dropdown on route navigation (SM-3337)
        this.showDropdown.set(false);
      });
  }

  toggleDropdown(): void {
    this.showDropdown.update(v => !v);
  }

  confirmSignout(): void {
    if (window.confirm('Are you sure you want to sign out?')) {
      this.logout();
    }
  }

  logout(): void {
    // Use AuthService to properly sign out from MAG via WS-Federation
    this.authService.logout();
  }

  ngOnDestroy(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  private readUserDetails(): LoggedInUser | null {
    const raw = sessionStorage.getItem('loggedInUserDetails');
    return raw ? (JSON.parse(raw) as LoggedInUser) : null;
  }

  private loadLogo(): void {
    this.generalService.getLogo()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(blob => {
        if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
        this.objectUrl = URL.createObjectURL(blob);
        this.logo.set(this.objectUrl);
      });
  }

  private loadApplicationName(): void {
    // SM-3426: getSettingValueByKey() routes to the Supplier-area endpoint for Supplier logins,
    // so this is safe for all roles (no more 403 on the Buyer endpoint).
    this.helperService.getSettingValueByKey('DefaultApplicationName')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        const result = response as SettingValueResponse;
        if (result?.success && result.data) {
          this.applicationName.set(result.data);
        }
      });
  }
}
